import { Injectable } from "@angular/core";
import { HttpService } from "./http.service";
import { environment } from "@environments/environment";
import { objectToQueryString } from "../utils/api.utils";
import { apiLogger } from "./api-logger";

function stripEmptyStrings<T>(body: T): T {
  if (body === null || body === undefined) return body;
  if (Array.isArray(body)) return body.map((item) => stripEmptyStrings(item)) as unknown as T;
  if (typeof body !== "object") return body;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
    if (value === "") continue;
    result[key] = stripEmptyStrings(value);
  }
  return result as T;
}

@Injectable({
  providedIn: "root",
})
export class ApiService {
  constructor(private readonly httpService: HttpService) {}

  public sendDeleteRequest<Response, Arguments extends Record<string, string | string[]> | undefined = undefined>(
    endpoint: string,
    queryStringArguments: Arguments,
  ): Promise<Response> {
    const path = this.buildBackendApiUrl(endpoint, queryStringArguments);
    return apiLogger.wrap("DELETE", endpoint, () => this.httpService.sendDeleteRequest<Response>(path));
  }

  public sendGetRequest<Response, Arguments extends Record<string, string | string[]> | undefined = undefined>(
    endpoint: string,
    queryStringArguments: Arguments,
  ): Promise<Response> {
    const path = this.buildBackendApiUrl(endpoint, queryStringArguments);
    return apiLogger.wrap("GET", endpoint, () => this.httpService.sendGetRequest<Response>(path), queryStringArguments);
  }

  public sendPostRequest<Input, Response, Arguments extends Record<string, string> | undefined = undefined>(
    endpoint: string,
    body: Input,
    queryStringArguments?: Arguments,
  ): Promise<Response> {
    const path = this.buildBackendApiUrl(endpoint, queryStringArguments);
    const cleanBody = stripEmptyStrings(body);
    return apiLogger.wrap(
      "POST",
      endpoint,
      () => this.httpService.sendPostRequest<Input, Response>(path, cleanBody),
      cleanBody,
    );
  }

  public sendPostRequestWithLoading<Input, Response, Arguments extends Record<string, string> | undefined = undefined>(
    endpoint: string,
    body: Input,
    queryStringArguments?: Arguments,
  ): Promise<Response> {
    const path = this.buildBackendApiUrl(endpoint, queryStringArguments);
    const cleanBody = stripEmptyStrings(body);
    return apiLogger.wrap(
      "POST",
      endpoint,
      () => this.httpService.sendPostRequestWithLoading<Input, Response>(path, cleanBody),
      cleanBody,
    );
  }

  public sendPatchRequest<Input, Response, Arguments extends Record<string, string> | undefined = undefined>(
    endpoint: string,
    body: Input,
    queryStringArguments?: Arguments,
  ): Promise<Response> {
    const path = this.buildBackendApiUrl(endpoint, queryStringArguments);
    const cleanBody = stripEmptyStrings(body);
    return apiLogger.wrap(
      "PATCH",
      endpoint,
      () => this.httpService.sendPatchRequest<Input, Response>(path, cleanBody),
      cleanBody,
    );
  }

  public sendPutRequest<Input, Response, Arguments extends Record<string, string> | undefined = undefined>(
    endpoint: string,
    body: Input,
    queryStringArguments?: Arguments,
  ): Promise<Response> {
    const path = this.buildBackendApiUrl(endpoint, queryStringArguments);
    const cleanBody = stripEmptyStrings(body);
    return apiLogger.wrap(
      "PUT",
      endpoint,
      () => this.httpService.sendPutRequest<Input, Response>(path, cleanBody),
      cleanBody,
    );
  }

  private buildBackendApiUrl<Arguments extends Record<string, string | string[]> | undefined>(
    endpoint: string,
    queryStringArguments?: Arguments,
  ): URL {
    const backendUrl =
      localStorage.getItem("FORCE_LOCAL_DOMAIN") === "true"
        ? new URL("http://localhost:3001")
        : environment.endpoints.backendDomain;

    const queryString = objectToQueryString(queryStringArguments);
    const suffix = queryString ? `?${queryString}` : "";
    return new URL(`${backendUrl}${endpoint}${suffix}`);
  }
}
